// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ProgressBar from './ProgressBar';
import Button from './Button';
import ShipVisualization from './ShipVisualization';
import { useShipHealth } from '../contexts/ShipHealthContext';
import { useCredits } from '../contexts/CreditsContext';
import GameConfig from '../config/GameConfig';
import { getDifficultyMultipliers } from '../utils/DifficultyUtils';
import './CompactSystemStatus.css';
import EventBus, { EVENT_TYPES } from '../utils/system/EventBus';

const CompactSystemStatus = ({ power, onMaximize, showRepairButton = true, onOpenAlertHistory }) => {
  const { systems: shipSystems, getOverallHealth, repairAll } = useShipHealth();
  const { credits, removeCredits } = useCredits();
  
  const [systems, setSystems] = useState([]);
  const [overallHealth, setOverallHealth] = useState(100);
  const [repairCost, setRepairCost] = useState(0);
  const [sparks, setSparks] = useState([]);
  const [showRepairModal, setShowRepairModal] = useState(false);
  const [repairMessage, setRepairMessage] = useState({ title: '', message: '', isError: false });

  // Calculate repair cost for all systems
  const calculateTotalRepairCost = (systemsList) => {
    const totalDamage = systemsList.reduce((sum, sys) => {
      return sum + (sys.maxHealth - sys.health);
    }, 0);
    return Math.ceil(totalDamage * GameConfig.credits.repairCostPerHP * getDifficultyMultipliers().repairCostMultiplier);
  };

  // Load ship systems and subscribe to repair events
  useEffect(() => {
    const updateSystems = () => {
      setSystems(shipSystems.map((sys, index) => {
        const criticalThreshold = GameConfig.shipHealth.criticalThresholds[sys.id.toLowerCase()] || 30;
        return {
          ...sys,
          status: sys.health <= 0 ? 'DESTROYED' : 
                  sys.health <= criticalThreshold ? 'CRITICAL' :
                  sys.health < sys.maxHealth ? 'DAMAGED' : 'OPTIMAL',
          damaged: sys.health < sys.maxHealth,
          critical: sys.health <= criticalThreshold,
          // Positions for compact ship (200x200)
          x: [50, 50, 20, 50, 50, 80, 50][index] || 50,  
          y: [50, 20, 70, 80, 30, 70, 40][index] || 50
        };
      }));
      
      // Calculate repair cost
      setRepairCost(calculateTotalRepairCost(shipSystems));
      
      // Update overall health
      setOverallHealth(getOverallHealth());
      
      // Create sparks for critical systems
      shipSystems.forEach((sys, index) => {
        const criticalThreshold = GameConfig.shipHealth.criticalThresholds[sys.id.toLowerCase()] || 30;
        const isCritical = sys.health <= criticalThreshold;
        if (isCritical && Math.random() < GameConfig.ui.sparkGenerationChance) {
          const sparkId = `${Date.now()}-${Math.random()}`;
          setSparks(prev => [...prev, {
            id: sparkId,
            x: [50, 50, 20, 50, 50, 80, 50][index],
            y: [50, 20, 70, 80, 30, 70, 40][index]
          }]);
          
          setTimeout(() => {
            setSparks(prev => prev.filter(s => s.id !== sparkId));
          }, 500);
        }
      });
    };

    updateSystems();
    const interval = setInterval(updateSystems, 500);
    
    // Listen for SystemStatus modal closing
    const handleModalClose = () => {
      updateSystems();
    };
    const unsubSystemStatus = EventBus.on(EVENT_TYPES.SYSTEM_STATUS_CLOSED, handleModalClose);
    
    return () => {
      clearInterval(interval);
      unsubSystemStatus();
    };
  }, [shipSystems, getOverallHealth]);

  const handleQuickRepair = () => {
    const cost = calculateTotalRepairCost(shipSystems);
    
    if (cost === 0) {
      setRepairMessage({
        title: '✓ ALL SYSTEMS OPTIMAL',
        message: 'All systems are at full health!',
        isError: false
      });
      setShowRepairModal(true);
      return;
    }
    
    if (credits < cost) {
      setRepairMessage({
        title: '⚠️ INSUFFICIENT CREDITS',
        message: `Need ${cost} credits to repair all systems.`,
        isError: true
      });
      setShowRepairModal(true);
      return;
    }
    
    // Deduct credits and repair all
    removeCredits(cost, 'repair all systems');
    repairAll();
    
    setRepairMessage({
      title: '🔧 REPAIR COMPLETE',
      message: `All systems repaired! Cost: ${cost} credits`,
      isError: false
    });
    setShowRepairModal(true);
  };

  const getSystemIcon = (system) => {
    if (system.status === 'CRITICAL' || system.status === 'DESTROYED') return '💥';
    if (system.status === 'DAMAGED') return '🔧';
    return '✓';
  };

  const getSystemColor = (health) => {
    if (health >= 80) return '#00ff88';
    if (health >= 50) return '#ffaa00';
    return '#ff4444';
  };

  return (
    <div className="compact-system-status">
      {/* Header with Overall Health */}
      <div className="compact-header">
        <div className="compact-title">◊ SHIP STATUS ◊</div>
        {onOpenAlertHistory && (
          <div className="compact-alert-button-container">
            <Button
              variant="danger"
              size="small"
              onClick={onOpenAlertHistory}
            >
              ALERTS
            </Button>
          </div>
        )}
        <div className="overall-health-compact">
          <div className="health-label">
            <span>OVERALL HEALTH</span>
            <span className={`health-status-badge ${
              overallHealth >= 80 ? 'optimal' : 
              overallHealth >= 50 ? 'damaged' : 'critical'
            }`}>
              {overallHealth >= 80 ? '✓ OPTIMAL' : 
               overallHealth >= 50 ? '⚠ DAMAGED' : '💥 CRITICAL'}
            </span>
          </div>
          <ProgressBar 
            value={overallHealth}
            max={100}
            color="auto"
            showLabel={true}
            animated={overallHealth < 50}
            size="small"
          />
        </div>
      </div>

      {/* Ship Visualization */}
      <div className="compact-ship-container" onClick={onMaximize} style={{ cursor: 'pointer' }}>
        <div style={{
          transform: 'scale(0.65)',
          transformOrigin: 'center center',
          width: '400px',
          height: '400px',
          margin: '-70px auto'
        }}>
          <ShipVisualization 
            systems={systems}
            rotation={0}
            sparks={sparks}
            onSystemClick={null}
          />
        </div>
      </div>

      {/* System Pills */}
      <div className="system-pills">
        {systems.map(system => (
          <div 
            key={system.id}
            className="system-pill"
            style={{ 
              borderColor: getSystemColor(system.health),
              background: `${getSystemColor(system.health)}15`
            }}
          >
            <span className="pill-icon">{getSystemIcon(system)}</span>
            <span className="pill-name">{system.name.split('_')[0]}</span>
            <span 
              className="pill-health"
              style={{ color: getSystemColor(system.health) }}
            >
              {Math.round(system.health || 0)}%
            </span>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="compact-actions">
        {showRepairButton && (
          <button 
            className="compact-repair-button"
            onClick={handleQuickRepair}
            disabled={repairCost === 0}
            style={{
              opacity: repairCost === 0 ? 0.5 : 1,
              cursor: repairCost === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            {repairCost === 0 ? '✓ ALL OPTIMAL' : `🔧 REPAIR (${repairCost} CREDITS)`}
          </button>
        )}
        <button 
          className="compact-maximize-button"
          onClick={onMaximize}
        >
          ⛶ DETAILS
        </button>
      </div>

      {/* Repair Result Modal */}
      {showRepairModal && createPortal(
        <div className="repair-confirm-overlay" onClick={() => setShowRepairModal(false)}>
          <div className="repair-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="repair-confirm-header">
              <div className="repair-confirm-title">{repairMessage.title}</div>
            </div>
            
            <div className="repair-confirm-content">
              <div className={repairMessage.isError ? "repair-confirm-error" : "repair-confirm-message"}>
                {repairMessage.message}
              </div>
            </div>
            
            <div className="repair-confirm-actions">
              <button 
                className="repair-confirm-button cancel"
                onClick={() => setShowRepairModal(false)}
              >
                CLOSE
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CompactSystemStatus;
